import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Camera, CameraOff, Keyboard, Loader2, Bug, ScanBarcode, Usb } from 'lucide-react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';

// App labels are Code 128; keep the common 1D + QR set as a safety net.
const FORMATS = [
  BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.CODE_93, BarcodeFormat.CODABAR,
  BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.ITF, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
  BarcodeFormat.QR_CODE,
];

function buildReader() {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, FORMATS);
  hints.set(DecodeHintType.TRY_HARDER, true);
  return new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 120 });
}

export function BarcodeScanner({ open, onClose, onDetected }) {
  const [mode, setMode] = useState('camera'); // 'camera' | 'gun'
  const [manual, setManual] = useState('');
  const [gun, setGun] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('idle'); // idle | starting | scanning | error
  const [debug, setDebug] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  const videoRef = useRef(null);
  const controlsRef = useRef(null);   // ZXing IScannerControls
  const readerRef = useRef(null);
  const gunInputRef = useRef(null);
  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;

  const stopCamera = useCallback(() => {
    try { controlsRef.current && controlsRef.current.stop(); } catch (e) { /* noop */ }
    controlsRef.current = null;
    const v = videoRef.current;
    if (v && v.srcObject) {
      try { v.srcObject.getTracks().forEach((t) => t.stop()); } catch (e) { /* noop */ }
      try { v.srcObject = null; } catch (e) { /* noop */ }
    }
    setStatus('idle');
  }, []);

  const finish = useCallback((code) => {
    const value = String(code || '').trim();
    if (!value) return;
    stopCamera();
    onDetectedRef.current(value);
  }, [stopCamera]);

  const gatherDebug = useCallback(async () => {
    const md = navigator.mediaDevices;
    const info = {
      isSecureContext: typeof window.isSecureContext === 'boolean' ? window.isSecureContext : 'unknown',
      standalone: (window.navigator.standalone === true) || (!!window.matchMedia && window.matchMedia('(display-mode: standalone)').matches),
      hasMediaDevices: !!md,
      hasGetUserMedia: !!(md && md.getUserMedia),
      engine: 'ZXing (works on iPhone Safari)',
      videoInputs: [],
      devicesError: null,
    };
    try {
      if (md && md.enumerateDevices) {
        const list = await md.enumerateDevices();
        info.videoInputs = list.filter((d) => d.kind === 'videoinput').map((d, i) => ({ label: d.label || `Camera ${i + 1}` }));
      }
    } catch (e) { info.devicesError = `${(e && e.name) || 'Error'}: ${(e && e.message) || 'Unknown'}`; }
    setDebug(info); setShowDebug(true);
    return info;
  }, []);

  // Started only from a user gesture (iOS requires it).
  const startCamera = useCallback(async () => {
    setError('');
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      await gatherDebug();
      setError('Camera (getUserMedia) is not available here. Ensure the address starts with https://, allow camera access, or use Scanner Gun / manual entry below.');
      setStatus('error');
      return;
    }
    setStatus('starting');
    try {
      readerRef.current = buildReader();
      const constraints = { audio: false, video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } } };
      controlsRef.current = await readerRef.current.decodeFromConstraints(constraints, videoRef.current, (result, err, controls) => {
        if (result) { controls.stop(); finish(result.getText()); }
      });
      // Try to enable continuous autofocus where supported (best-effort, ignored on iOS).
      try {
        const track = videoRef.current && videoRef.current.srcObject && videoRef.current.srcObject.getVideoTracks()[0];
        if (track && track.applyConstraints) { await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {}); }
      } catch (e) { /* noop */ }
      setStatus('scanning');
    } catch (e) {
      const name = (e && e.name) || 'Error';
      const msg = (e && e.message) || 'Unknown error';
      setError(`${name}: ${msg}`);
      setStatus('error');
      await gatherDebug();
    }
  }, [finish, gatherDebug]);

  // Cleanup on close / unmount.
  useEffect(() => {
    if (!open) {
      stopCamera(); setError(''); setManual(''); setGun(''); setDebug(null); setShowDebug(false); setMode('camera');
    }
    return () => stopCamera();
  }, [open, stopCamera]);

  // Keep the scanner-gun input focused so hardware scanners "type" into it.
  useEffect(() => {
    if (open && mode === 'gun') {
      stopCamera();
      const t = setTimeout(() => { try { gunInputRef.current && gunInputRef.current.focus(); } catch (e) { /* noop */ } }, 120);
      return () => clearTimeout(t);
    }
  }, [open, mode, stopCamera]);

  const submitManual = (e) => { e.preventDefault(); if (manual.trim()) { finish(manual); setManual(''); } };
  const onGunKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); const v = gun.trim(); if (v) { finish(v); setGun(''); } }
  };
  const handleClose = () => { stopCamera(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="barcode-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><ScanBarcode className="h-5 w-5" /> Scan Barcode</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2">
            <Button variant={mode === 'camera' ? 'default' : 'outline'} onClick={() => setMode('camera')} data-testid="scan-mode-camera"><Camera className="h-4 w-4 mr-2" /> Camera</Button>
            <Button variant={mode === 'gun' ? 'default' : 'outline'} onClick={() => setMode('gun')} data-testid="scan-mode-gun"><Usb className="h-4 w-4 mr-2" /> Scanner Gun</Button>
          </div>

          {mode === 'gun' && (
            <div className="space-y-2" data-testid="scanner-gun-panel">
              <label className="flex items-center gap-2 text-sm font-medium"><Usb className="h-4 w-4" /> Bluetooth / USB Scanner</label>
              <p className="text-xs text-muted-foreground">Keep this box focused and pull the trigger. Most scanner guns send an Enter automatically — the product is looked up instantly.</p>
              <Input
                ref={gunInputRef}
                autoFocus
                value={gun}
                onChange={(e) => setGun(e.target.value)}
                onKeyDown={onGunKeyDown}
                onBlur={() => { if (open && mode === 'gun') setTimeout(() => { try { gunInputRef.current && gunInputRef.current.focus(); } catch (e) { /* noop */ } }, 50); }}
                placeholder="Waiting for scanner…"
                data-testid="scanner-gun-input"
              />
              <Button className="w-full" onClick={() => { const v = gun.trim(); if (v) { finish(v); setGun(''); } }} data-testid="scanner-gun-submit">Find Product</Button>
            </div>
          )}

          {mode === 'camera' && (
            <>
              <div className="relative w-full overflow-hidden rounded-lg bg-slate-900 aspect-[4/3] flex items-center justify-center">
                <video ref={videoRef} playsInline muted autoPlay className={`h-full w-full object-cover ${status === 'scanning' ? '' : 'hidden'}`} data-testid="barcode-scan-video" />
                {status !== 'scanning' && (
                  <div className="px-4 text-center text-white/70">
                    {status === 'starting'
                      ? <span className="inline-flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Starting camera…</span>
                      : <span className="inline-flex items-center gap-2 text-sm"><Camera className="h-4 w-4" /> Tap “Start Camera” to scan</span>}
                  </div>
                )}
                {status === 'scanning' && (
                  <div className="pointer-events-none absolute inset-x-8 top-1/2 h-0.5 -translate-y-1/2 rounded bg-emerald-400/80 shadow-[0_0_12px_2px_rgba(52,211,153,0.6)]" />
                )}
              </div>

              {status !== 'scanning' ? (
                <Button className="w-full" onClick={startCamera} disabled={status === 'starting'} data-testid="barcode-scan-start-button">
                  {status === 'starting' ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…</>) : (<><Camera className="h-4 w-4 mr-2" /> Start Camera</>)}
                </Button>
              ) : (
                <Button variant="outline" className="w-full" onClick={stopCamera} data-testid="barcode-scan-stop-button"><CameraOff className="h-4 w-4 mr-2" /> Stop Camera</Button>
              )}
              {status === 'scanning' && (
                <p className="text-center text-xs text-muted-foreground">Point the rear camera at the label barcode. Hold steady ~15–20cm away.</p>
              )}
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="barcode-scan-error">
              <CameraOff className="mt-0.5 h-4 w-4 shrink-0" /> <span className="break-words">{error}</span>
            </div>
          )}

          {/* Manual fallback — always available */}
          <form onSubmit={submitManual} className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium"><Keyboard className="h-4 w-4" /> Manual SKU / Barcode</label>
            <div className="flex gap-2">
              <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="e.g. KN-16-00136" data-testid="barcode-scan-manual-input" />
              <Button type="submit" data-testid="barcode-scan-manual-submit">Find</Button>
            </div>
          </form>

          {/* Diagnostics */}
          <div className="rounded-md border bg-muted/40 p-3">
            <button type="button" onClick={() => (showDebug ? setShowDebug(false) : gatherDebug())} className="flex w-full items-center justify-between text-xs font-medium text-muted-foreground hover:text-foreground" data-testid="barcode-scan-debug-toggle">
              <span className="flex items-center gap-1.5"><Bug className="h-3.5 w-3.5" /> Camera diagnostics</span>
              <span>{showDebug ? 'Hide' : 'Show'}</span>
            </button>
            {showDebug && debug && (
              <div className="mt-2 space-y-1 text-[11px] leading-relaxed text-muted-foreground" data-testid="barcode-scan-debug-panel">
                <div>Secure context (https): <b className={debug.isSecureContext === true ? 'text-emerald-700' : 'text-red-600'}>{String(debug.isSecureContext)}</b></div>
                <div>Standalone (Home-Screen app): <b>{String(debug.standalone)}</b></div>
                <div>getUserMedia: <b className={debug.hasGetUserMedia ? 'text-emerald-700' : 'text-red-600'}>{String(debug.hasGetUserMedia)}</b></div>
                <div>Decoder: <b>{debug.engine}</b></div>
                <div>Video inputs: <b>{debug.videoInputs.length}</b></div>
                {debug.videoInputs.map((d, i) => (<div key={`vid-${i}`} className="pl-3">• {d.label}</div>))}
                {debug.devicesError && <div className="text-red-600">enumerateDevices: {debug.devicesError}</div>}
              </div>
            )}
          </div>

          <Button variant="outline" className="w-full" onClick={handleClose} data-testid="barcode-scan-close-button">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
